import { createContext, useContext, useState, useEffect, useMemo } from 'react'
import type { ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import Cookies from 'js-cookie'
import { decodeAuthToken } from '@/lib/utils/authUtils'
import { clearOtpConfirmed } from '@/lib/utils/authState'
import { purgeLegacyStorage } from '@/lib/utils/legacyStorage'
import { claimHandoff } from '@/lib/utils/agentHandoff'

const HANDOFF_PENDING_COOKIE = 'hb_agent_pending'
const HANDOFF_CLAIM_TIMEOUT_MS = 5_000

interface AuthContextType {
  token: string | null
  setToken: (token: string) => void
  logout: () => void
  isLoading: boolean
  user: { name?: string; email?: string; scriptId?: string; userId?: string } | null
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  // Read token synchronously on mount — also check ?authToken= URL param (impersonation links)
  const [token, setTokenState] = useState<string | null>(() => {
    const params = new URLSearchParams(window.location.search)
    const urlToken = params.get('authToken')
    if (urlToken) {
      Cookies.set('authToken', urlToken, { expires: 30, sameSite: 'lax', secure: process.env.NODE_ENV === 'production' })
      params.delete('authToken')
      const newUrl = [window.location.pathname, params.toString() ? `?${params.toString()}` : '', window.location.hash].join('')
      window.history.replaceState(null, '', newUrl)
      return urlToken
    }
    return Cookies.get('authToken') || null
  })
  const [isLoading, setIsLoading] = useState(false)
  // Held until the claim answers, so route guards don't send someone to sign-in
  // a moment before their session arrives.
  const [claimingHandoff, setClaimingHandoff] = useState(() => !token && Boolean(Cookies.get(HANDOFF_PENDING_COOKIE)))

  // Decode user data from token
  const user = useMemo(() => {
    if (!token) return null
    try {
      const decoded = decodeAuthToken(token)
      return {
        name: decoded.name || undefined,
        email: decoded.email || undefined,
        scriptId: decoded.scriptId || undefined,
        userId: decoded.userId || undefined,
      }
    } catch {
      return null
    }
  }, [token])

  useEffect(() => {
    if (user) {
      Sentry.setUser({ id: user.userId, email: user.email })
      return
    }

    Sentry.setUser(null)
  }, [user])

  const setToken = (newToken: string) => {
    setTokenState(newToken)
    Cookies.set('authToken', newToken, {
      expires: 30, // 30 days
      sameSite: 'lax',
      secure: process.env.NODE_ENV === 'production'
    })
  }

  const logout = () => {
    setTokenState(null)
    Cookies.remove('authToken')
    // Logging out must stay logged out: no handoff may sign this browser back in on the next load.
    Cookies.remove(HANDOFF_PENDING_COOKIE)
    clearOtpConfirmed()
    purgeLegacyStorage()
    Sentry.setUser(null)
    // Navigate to sign-in page - will be handled by router guard
  }

  useEffect(() => {
    if (!claimingHandoff) return
    const controller = new AbortController()
    const timer = window.setTimeout(() => controller.abort(), HANDOFF_CLAIM_TIMEOUT_MS)
    claimHandoff(controller.signal).then((claimed) => {
      if (claimed) setToken(claimed)
      setClaimingHandoff(false)
    })
    return () => {
      window.clearTimeout(timer)
    }
  }, [claimingHandoff])

  if (claimingHandoff) return null

  return (
    <AuthContext.Provider value={{ token, setToken, logout, isLoading, user }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

/**
 * Minimal AuthProvider for direct/standalone mode (e.g. editor injected into customer site).
 * Accepts a pre-existing JWT token (e.g. from URL params) and satisfies AuthContext
 * without any login flow or cookie management. All API calls using the token
 * will be fully authenticated.
 */
export function DirectModeAuthProvider({ token, children }: { token: string | null; children: ReactNode }) {
  const user = useMemo(() => {
    if (!token) return null
    try {
      const decoded = decodeAuthToken(token)
      return {
        name: decoded.name || undefined,
        email: decoded.email || undefined,
        scriptId: decoded.scriptId || undefined,
        userId: decoded.userId || undefined,
      }
    } catch {
      return null
    }
  }, [token])

  useEffect(() => {
    if (user) {
      Sentry.setUser({ id: user.userId, email: user.email })
      return
    }

    Sentry.setUser(null)
  }, [user])

  return (
    <AuthContext.Provider value={{
      token,
      setToken: () => {},
      logout: () => {},
      isLoading: false,
      user,
    }}>
      {children}
    </AuthContext.Provider>
  )
}